import { useMemo } from "react";
import type {
  Category,
  CategoryFilter,
  Task,
  PlanEvent,
} from "../shared/types";
import { getAllCategories } from "../shared/stores/category-store";
import { expandRecurringEvents } from "../features/events/utils/expand-recurring";
import { addDays, getToday } from "../shared/types/date-helpers";

export function useAppFiltering(
  activeTasks: Task[],
  events: PlanEvent[],
  activeFilter: CategoryFilter,
  hideHidden: boolean,
) {
  const visibleTasks = useMemo(
    () =>
      hideHidden
        ? activeTasks.filter((t) => !t.tags.includes("hidden"))
        : activeTasks,
    [activeTasks, hideHidden],
  );

  const visibleEvents = useMemo(
    () =>
      hideHidden
        ? events.filter(
            (e) => !(e.tags as readonly string[]).includes("hidden"),
          )
        : events,
    [events, hideHidden],
  );

  const filteredTasks = useMemo(
    () =>
      activeFilter === "all"
        ? visibleTasks
        : visibleTasks.filter((t) => t.category === activeFilter),
    [visibleTasks, activeFilter],
  );

  const expandedEvents = useMemo(() => {
    const today = getToday();
    const recurring = visibleEvents.filter((e) => e.recurrence);
    const nonRecurring = visibleEvents.filter((e) => !e.recurrence);
    const expandedRecurring = expandRecurringEvents(
      recurring,
      addDays(today, -90),
      addDays(today, 365),
    );
    return [...nonRecurring, ...expandedRecurring].sort((a, b) => {
      if (a.date !== b.date) return a.date.localeCompare(b.date);
      return a.startTime.localeCompare(b.startTime);
    });
  }, [visibleEvents]);

  const filteredEvents = useMemo(
    () =>
      activeFilter === "all"
        ? expandedEvents
        : expandedEvents.filter((e) => e.category === activeFilter),
    [expandedEvents, activeFilter],
  );

  const counts = useMemo(() => {
    const result = {} as Record<Category, number>;
    const allCategories = getAllCategories();
    for (const cat of allCategories) {
      result[cat as Category] =
        visibleTasks.filter((t) => t.category === cat).length +
        visibleEvents.filter((e) => e.category === cat).length;
    }
    return result;
  }, [visibleTasks, visibleEvents]);

  const completionRates = useMemo(() => {
    const result = {} as Record<Category, number>;
    const allCategories = getAllCategories();
    for (const cat of allCategories) {
      const catTasks = visibleTasks.filter((t) => t.category === cat);
      const done = catTasks.filter((t) => t.status === "done").length;
      result[cat as Category] =
        catTasks.length === 0 ? 0 : Math.round((done / catTasks.length) * 100);
    }
    return result;
  }, [visibleTasks]);

  const totalCount = visibleTasks.length + visibleEvents.length;
  const defaultCategory: Category =
    activeFilter === "all" ? "personal" : activeFilter;

  return {
    visibleTasks,
    visibleEvents,
    filteredTasks,
    filteredEvents,
    expandedEvents,
    counts,
    completionRates,
    totalCount,
    defaultCategory,
  };
}
